/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import {Form ,Button ,Table ,Modal, Alert} from 'react-bootstrap';
import { ActionMeta, components } from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPenToSquare, faCircleXmark, faSort, faPlay, faSortUp, faSortDown, faFileArrowDown, faCheckDouble, faArrowRight, faMagnifyingGlass, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { RootState } from '../../../redux/rootReducer';
import { addListsRequest, deleteListsRequest, getListsRequest, clearListData } from '../../../redux/modules/lists/listsSlice';
import { API_ENDPOINTS } from '../../../constants/api';
import { addListParams ,Option } from '../../../redux/modules/lists/types';
import { toast } from 'react-toastify';
import { tableHeadings_List } from '../../../constants/constants';
import XDeleteModal from '../../../component/modal/XDeleteModal';
import useSidebar from '../../../hooks/useSidebar';
import { getPredefinedFieldRequest } from '../../../redux/modules/settings/settingsSlice';
import XLoader from '../../../component/loader/XLoader';
import XButton from '../../../component/button/XButton';
import PaginationNav from '../../../component/pagination/pagination';
import { toaster } from '../../../utils/toast';
import { debounceAsync, downloadCsv, formatDate } from "../../../utils/helper";
import { columnTypes, PermissionObject } from '../../../redux/modules/auth/types';
import { ROLE } from '../../../utils/enums';
import AsyncSelect from "react-select/async";
import useAxios from '../../../hooks/useAxios';
import { Col, Row} from 'react-bootstrap';
import useUploadProgress from '../../../hooks/useUploadProgress';
import XConfirm from '../../../component/confirm/XConfirm';

const MAX_DISPLAYED_CHIPS = 30;

interface ContactsViewerModalProps {
  show: boolean;
  onHide: () => void;
  contacts: Option[];
  onRemoveContact: (value: string) => void;
}

const PAGE_SIZE = 50;

const ContactsViewerModal: React.FC<ContactsViewerModalProps> = React.memo(({
  show,
  onHide,
  contacts,
  onRemoveContact,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [show]);

  const filteredContacts = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return contacts;
    }
    const term = searchTerm.toLowerCase().trim();
    return contacts.filter((opt: any) =>
      opt?.label?.toLowerCase().includes(term)
    );
  }, [contacts, searchTerm]);

  const totalContacts = filteredContacts.length;
  const totalPages = Math.max(1, Math.ceil(totalContacts / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedContacts = React.useMemo(() => {
    const startIndex = (safePage - 1) * PAGE_SIZE;
    return filteredContacts.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredContacts, safePage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const startRecord = totalContacts === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(safePage * PAGE_SIZE, totalContacts);

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="contacts-viewer-modal"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Contacts List</Modal.Title>
          <div className="text-muted" style={{ fontSize: '14px', fontWeight: 400, marginTop: '2px' }}>
            Manage and view all contacts included in this list
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* Search Bar & Total Counter */}
        <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
          <div className="position-relative flex-grow-1">
            <Form.Control
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#13294B',
                pointerEvents: 'none',
              }}
            />
          </div>
          <div className="text-nowrap">
            <span
              className="badge px-3 py-2"
              style={{
                backgroundColor: '#edf2f7',
                color: '#13294B',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '8px',
                border: '1px solid #d0dbe7',
              }}
            >
              Total: {contacts.length.toLocaleString()} Contacts
            </span>
          </div>
        </div>

        {/* Result summary */}
        <div className="d-flex justify-content-between align-items-center mb-2 text-muted" style={{ fontSize: '14px' }}>
          <span>
            {searchTerm
              ? `Found ${totalContacts.toLocaleString()} matching contact${totalContacts === 1 ? '' : 's'}`
              : `Showing ${startRecord.toLocaleString()} - ${endRecord.toLocaleString()} of ${totalContacts.toLocaleString()} contacts`}
          </span>
          {totalPages > 1 && (
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              Page {safePage} of {totalPages}
            </span>
          )}
        </div>

        {/* Contacts Table */}
        <div
          ref={tableContainerRef}
          className="table-listing position-relative contacts-scroll-container"
          style={{
            padding: 0,
            backgroundColor: '#fff',
          }}
        >
          <Table responsive className="mb-0">
            <thead>
              <tr>
                <th>Email Address</th>
                <th style={{ width: '130px', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedContacts.length === 0 ? (
                <tr>
                  <td colSpan={2}>
                    <p className="text-center no-records mb-0 py-4">
                      {searchTerm
                        ? `No contacts found matching "${searchTerm}"`
                        : 'No contacts in this list.'}
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedContacts.map((contact: Option, idx: number) => {
                  return (
                    <tr key={contact.value || idx}>
                      <td>{contact.label}</td>
                      <td className="action-badges" style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{ padding: '6px 16px', fontSize: '13px' }}
                          onClick={() => onRemoveContact(contact.value)}
                        >
                          <span className="d-flex align-items-center justify-content-center">
                            <span>Delete</span> <FontAwesomeIcon icon={faCircleXmark} />
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <Button
          variant="primary"
          className="btn-theme btn-border"
          onClick={onHide}
        >
          Close
        </Button>
        {totalPages > 1 && (
          <div className="d-flex align-items-center modal-pagination-wrapper">
            <PaginationNav
              totalPages={totalPages}
              currentPage={safePage}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
});

interface ListProps {
  permission?: PermissionObject
}

const Lists: React.FC<ListProps> = (props) => {
  const { permission } = props;
  const dispatch = useDispatch();
  const { list ,successRes ,listRes ,errorRes, loading} = useSelector((state: RootState) => state.lists);
  const [show, setShow] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [queryParams, setQueryParams] = useState({search: '',page: 1,limit: 10,});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [listId, setListId] = useState<string>('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedList, setSelectedList] = useState<any| null>(null);
  const { onShowSidebar } = useSidebar();
  const { predefinedFieldList } = useSelector((state: RootState) => state.settings);
  const { orgLocations, selectedLocationId, role_id } = useSelector((state: RootState) => state.user);
  const isReadOnly = +role_id === ROLE.READ_ONLY;

  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);

  const locationTimezone = activeLocation?.location_timezone || 'UTC';
  const { postData, fetchData } = useAxios();

  const {
    progress: uploadProgress,
    isUploading,
    startUpload,
    resetUpload,
  } = useUploadProgress({
    isLoading: loading,
    isSuccess: successRes === true,
    isError: Boolean(errorRes),
    fileSize: csvFile?.size,
  });
  const [existingFields, setExistingFields] = useState<string[]>([]);
  const [preselectedValue, setPreselectedValue] = useState<{ csvField: string; databaseField: string }[]>([]);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);
  const [showCsvConfirmModal, setShowCsvConfirmModal] = useState(false);
  const [pendingListPayload, setPendingListPayload] = useState<any>(null);
  const [csvFields, setCsvFields] = useState<string[]>([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const unmappedFields = csvFields.filter((_: any, index: number) => {
    const mapped = preselectedValue[index];
    return !mapped || !mapped.databaseField || mapped.databaseField.trim() === "";
  });

  const handleRemoveContactFromSelected = React.useCallback((contactValue: string) => {
    setSelectedOptions((prev: any[]) => prev.filter((opt: any) => opt.value !== contactValue));
  }, []);

  const handleOpenContactsModal = React.useCallback(() => {
    setShowContactsModal(true);
  }, []);

  const displayedSelectValue = React.useMemo(() => {
    if (!selectedOptions || selectedOptions.length <= MAX_DISPLAYED_CHIPS) {
      return selectedOptions;
    }
    return selectedOptions.slice(0, MAX_DISPLAYED_CHIPS);
  }, [selectedOptions]);

  const CustomValueContainer = React.useCallback(
    ({ children, ...props }: any) => {
      const total = selectedOptions?.length || 0;
      const remaining = total - MAX_DISPLAYED_CHIPS;

      return (
        <components.ValueContainer {...props}>
          {children}
          {remaining > 0 && (
            <span
              className="badge bg-secondary text-white m-1 align-self-center py-1 px-2"
              style={{
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '4px',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowContactsModal(true);
              }}
              title="Click to view and search all contacts in this list"
            >
              +{remaining.toLocaleString()} more (View All)
            </span>
          )}
        </components.ValueContainer>
      );
    },
    [selectedOptions?.length]
  );

  useEffect(() => {
    dispatch(getListsRequest(queryParams));
    dispatch(getPredefinedFieldRequest());
  }, [queryParams, selectedLocationId])

  useEffect(() => {
    const totalCount = list?.totalCount;
    const totalPages = Math.ceil(totalCount / queryParams?.limit);
    setTotalPages(totalPages);
  }, [list?.totalCount])


  const validationSchema = Yup.object({
    listName: Yup.string().trim().required('List name is required').test(
      (value) => value.trim() !== ''
    ),
  });

  const handleProceedSaveList = () => {
    setShowCsvConfirmModal(false);
    if (csvFile && pendingListPayload) {
      const hasEmailMapping = preselectedValue?.some(
        (item) => item.databaseField && item.databaseField.trim().toLowerCase() === 'email'
      );
      if (!hasEmailMapping) {
        toaster({
          success: false,
          message: 'Email field mapping is required to import contacts.',
        });
        return;
      }
      const formData = new FormData();
      formData.append('listData', JSON.stringify(pendingListPayload));
      formData.append('csvFile', csvFile);
      formData.append('mappedData', JSON.stringify(preselectedValue));
      startUpload();
      dispatch(addListsRequest(formData));
    }
  };

  const formik = useFormik<addListParams>({
    initialValues: {
      listName: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values: addListParams) => {   
      const payload={
        ...values,
        selectedOptions,
        update_id:listId
      }
      if (!csvFile && selectedOptions.length === 0) {
        toaster({success: false, message: 'Please select at least one option from contacts or upload CSV file'});
      } else if (csvFile) {
        const hasEmailMapping = preselectedValue?.some(
          (item) => item.databaseField && item.databaseField.trim().toLowerCase() === 'email'
        );
        if (!hasEmailMapping) {
          toaster({
            success: false,
            message: 'Email field mapping is required to import contacts.',
          });
          return;
        }
        setPendingListPayload(payload);
        setShowCsvConfirmModal(true);
      } else {
        dispatch(addListsRequest(payload));
      }
    },
  });

  const customFieldValidationSchema = Yup.object({
    customFields: Yup.array().of(
      Yup.object().shape({
        columnName: Yup.string().trim()
          .required('Column Name is required')
          .matches(/^[A-Za-z_0-9 ]*$/, 'Column Name can only contain letters, space and underscores')
          .matches(/^[^0-9]*$/, 'Column Name cannot contain numbers'),
        columnType: Yup.string().required('Column Type is required'),
      })
    ),
  });

  const customFieldsFormik = useFormik({
    initialValues: {
      customFields: [{ columnName: '', columnType: '' }]
    },
    validationSchema: customFieldValidationSchema,
    onSubmit: async (values) => {
      const { message, success } = await postData(API_ENDPOINTS.ADD_CUSTOM_FIELD, values);
      toaster({ message, success });
      if (success) {
        setShowCustomFieldsModal(false);
        await getPredefinedAndCustomFieldsName()
        customFieldsFormik.resetForm();
      }
    },
  });

  useEffect(() => {
    const normalizedExistingFields = existingFields.map((field:any) =>
      field.toLowerCase().replace(/_/g, "").replace(/\s+/g, "")
    );

    const normalizedCsvFields = csvFields.map((field:any) =>
      field.toLowerCase().replace(/_/g, "").replace(/\s+/g, "")
    );

    const matchedFields = csvFields.map((csvField:any, index:any) => {
      const normalizedCsvField = normalizedCsvFields[index];
      const matchIndex = normalizedExistingFields.findIndex(
        (existingField:any) => existingField === normalizedCsvField
      );

      if (matchIndex !== -1) {
        return {
          databaseField: existingFields[matchIndex],
          csvField,
        };
      } else {
        return {
          databaseField: "",
          csvField,
        };
      }
    });

    setPreselectedValue(matchedFields);
  }, [existingFields, csvFields]);

  const getPredefinedAndCustomFieldsName = async () => {
    const response = await fetchData(API_ENDPOINTS.GET_PREDEFINED_FILEDS);
    if (response.success) {
      const predefinedFields = response.data.predefinedFields || [];
      const extractedCustomFields = response.data.customFields.map((field:any) => ({
        name: field.name,
        type: field.type === "text" ? "character varying" : field.type,
      }));
      
      const combinedNames = [
        ...predefinedFields.map((field: any) => field.name),
        ...extractedCustomFields.map((field: any) => field.name),
      ];
  
      setExistingFields(combinedNames);
    } else {
      setExistingFields([]);
    }
  }

  useEffect(() => {
    if (successRes === true) {
      dispatch(getListsRequest(queryParams));
      toast(listRes?.message || 'List saved successfully');
      if (isUploading) {
        setTimeout(() => {
          handleClose();
        }, 750);
      } else {
        handleClose();
      }
    }
    if (errorRes) {
      toaster({ success: false, message: errorRes?.message || 'An error occurred' });
      resetUpload();
      dispatch(clearListData());
    }
  }, [successRes, errorRes]);

  const sortedList = [...(list?.contactList || [])].sort((a, b) => {
    if (sortColumn === 'List Name') {
        const aValue = a.name.toLowerCase();
        const bValue = b.name.toLowerCase();
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else if(sortColumn === 'Contact Count') {
        const aValue = a['contact_count'];
        const bValue = b['contact_count'];
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
        const aValue = new Date(a.updated_at);
        const bValue = new Date(b.updated_at);
        return sortOrder === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
    }
  });

  const handlePageChange = (pageNumber: number) => {
    setQueryParams((prevParams) => ({ ...prevParams, page: pageNumber }));
  };

  const handleSortClick = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const handleDeleteList =(listId : string)=>{
    dispatch(deleteListsRequest({listId}));
    setShowDeleteModal(false)
  }

  const resetCsvUploadState = () => {
    setCsvFile(null);
    setCsvFields([]);
    setPreselectedValue([]);
    setExistingFields([]);
    setShowCsvConfirmModal(false);
    setPendingListPayload(null);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleShow = () => {
    resetCsvUploadState();
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    resetCsvUploadState();
    setSelectedOptions([]);
    setListId('');
    formik.resetForm();
    dispatch(clearListData());
  };

  const getAllContacts = async (value?: string) => {
    try {
      if (!value) {
        return []
      }
      const payload = {
        limit: 10,
        searchEmailString: value,
        excludeIds: []
      }
      payload.excludeIds = selectedOptions.map((option: { value: string }) => +option.value);
      const res = await postData(API_ENDPOINTS.GET_ALL_CONTACTS , payload)
      const data = res.contactList
      const formattedOptions: Option[] = data.map((item: any) => ({
        value: item.id.toString(),
        label: item.email,
      }));
      setOptions(formattedOptions);
      return formattedOptions
    }
    catch (error: any) {
      return []
    }
  }

  const debouncedGetAllContacts = debounceAsync(getAllContacts, 600);

  const handleSelectChange = (selected: any, actionMeta: ActionMeta<Option>) => {
    if (actionMeta.action === 'select-option' && actionMeta.option) {
      const newOption = actionMeta.option;
      if (newOption.value === 'all') {
        setSelectedOptions(options);
      } else {
        setSelectedOptions((prev: any[]) => {
          if (prev.some((o: any) => o.value === newOption.value)) return prev;
          return [newOption, ...prev];
        });
      }
    } else if (
      (actionMeta.action === 'remove-value' || actionMeta.action === 'pop-value') &&
      actionMeta.removedValue
    ) {
      const removed = actionMeta.removedValue;
      setSelectedOptions((prev: any[]) => prev.filter((o: any) => o.value !== removed.value));
    } else if (actionMeta.action === 'clear') {
      setSelectedOptions([]);
    } else {
      setSelectedOptions(selected || []);
    }
  };


  const handleEditList =(listId : string)=>{
    setListId(listId);
    resetCsvUploadState();
    dispatch(clearListData());
    const filterData =  filterDataById(listId);
    if(filterData[0]?.contact_count > 0){
      const manipulatedOptions: Option[] = (filterData[0].contacts || [])
        .filter((c: any) => c && c.value != null)
        .map((originalOption: { value: string; label: string; }) => {
          return {
            value: (originalOption.value).toString(),
            label: originalOption.label || '',
          };
        });
      setSelectedOptions(manipulatedOptions);
    } else {
      setSelectedOptions([]);
    }
    formik.setFieldValue('listName', filterData[0]?.name || '')
    setShow(true);
  }

  const filterDataById = (idToFilter: string) => {
    const filteredData = list?.contactList.filter((item: { id: string; }) => item.id === idToFilter);
    return filteredData;
  };
  const handleSelectAll = () => {
    setSelectedOptions((prevSelectedOptions:any) => [...prevSelectedOptions, ...options]);
    setOptions([]);
    // setSelectAllOpen(false);
  };

  const handleFileChange = (input: HTMLInputElement) => {
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      setCsvFields([]);
      setExistingFields([]);
      setPreselectedValue([]);
      
      if (file.size > 52428800) {
        toaster({ success: false, message: "File size exceeds the 50MB limit. Please upload a smaller CSV file." });
        if (input) input.value = '';
        setCsvFile(null);
        setCsvFields([]);
        return;
      }

      const reader = new FileReader();

      reader.onload = async () => {
        const result = reader.result as string;
        const lines = result.split("\n");
        const headers = lines[0].split(",").map(header => header.trim().replace(/^["']|["']$/g, ''));
        await getPredefinedAndCustomFieldsName();
        setCsvFields(headers);
        setCsvFile(file);
      };

      reader.onerror = () => {
        toaster({ success: false, message: "Unable to read the selected CSV file." });
        setCsvFile(null);
        setCsvFields([]);
        setExistingFields([]);
        setPreselectedValue([]);
        input.value = '';
      };

      // Slice first 64KB to read headers quickly without loading entire multi-megabyte file into memory
      reader.readAsText(file.slice(0, 65536));
    }
  };

  const handleFieldChange = (csvField: string, selectedDatabaseField: string, index: number) => {
    setPreselectedValue((prevValues: any) => {
      // Copy the existing array
      const updatedValues = [...prevValues];

      // Update the mapping for the specific index
      updatedValues[index] = { csvField, databaseField: selectedDatabaseField };

      return updatedValues;
    });
  };
 
  const handleDeleteIconClick = (list: any) => {
    setSelectedList(list);
    setShowDeleteModal(true);
  }

  const downloadCsvFormat = () => {
    downloadCsv(predefinedFieldList)
  };

  return (
    <div>
    <div className="main-title d-flex align-items-center justify-content-between">
      <h2 className='d-flex align-items-center'>
        <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}><FontAwesomeIcon icon={faBars} /></span>Lists</h2>
        <Button
          variant="primary"
          type="button"
          className="btn-theme"
          disabled={!permission?.edit || isReadOnly}
          onClick={!permission?.edit || isReadOnly ? undefined : () => { 
            setSelectedOptions([])
            setListId('')
            formik.resetForm();
            handleShow()
          }}
        >Create List
        </Button>
        <Modal show={show}
          onHide={()=>{
            handleClose()
            setCsvFile(null)
          }}
          centered>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title> {formik.values.listName ? "Edit Contact List" :"Create Contact List"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
           
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Contact List Name</Form.Label>
                <Form.Control type="text" placeholder="Enter"
                  name="listName"
                  value={formik.values.listName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.listName && !!formik.errors.listName}
                  required
                />
                <Form.Control.Feedback type="invalid">{formik.errors.listName}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
                <div className='d-flex justify-content-between align-items-center mb-1'>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">Select From Current Contacts</Form.Label>
                    <span
                      className="badge"
                      onClick={() => {
                        if (selectedOptions.length > 0) {
                          setShowContactsModal(true);
                        }
                      }}
                      style={{
                        backgroundColor: '#E9F0F4',
                        color: '#13294B',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: selectedOptions.length > 0 ? 'pointer' : 'default',
                      }}
                      title={selectedOptions.length > 0 ? "Click to view all contacts in this list" : undefined}
                    >
                      Total: {selectedOptions.length.toLocaleString()} {selectedOptions.length === 1 ? 'Contact' : 'Contacts'} (View All)
                    </span>
                  </div>
                  <Button onClick={handleSelectAll} className="csv-download-btn-style" disabled={options.length === 0}>
                  <FontAwesomeIcon 
                    icon={faCheckDouble}
                    className='me-2'
                  />
                  Select All Contacts
                  </Button>
                </div>
                <AsyncSelect
                  classNamePrefix="contacts-select"
                  value={displayedSelectValue}
                  onChange={handleSelectChange}
                  isMulti
                  defaultOptions
                  loadOptions={debouncedGetAllContacts}
                  placeholder="Search contacts here"
                  components={{
                    ValueContainer: CustomValueContainer,
                  }}
                  styles={{
                    valueContainer: (provided: any) => ({
                      ...provided,
                      maxHeight: '160px',
                      overflowY: 'auto',
                    }),
                  }}
                />
              </Form.Group>          
            <Form.Group className="form-group" controlId="exampleForm.ControlInput1">
              <div className='d-flex justify-content-between align-items-center'>
                <Form.Label>Import Contacts </Form.Label>
                <Button className="csv-download-btn-style" onClick={downloadCsvFormat}>
                  <FontAwesomeIcon
                    icon={faFileArrowDown}
                    onClick={downloadCsvFormat}
                    className='me-2'
                  />
                  Download CSV Format 
                </Button>
              </div>
              <Form.Control 
                ref={fileInputRef} 
                type="file" 
                onChange={(e) => handleFileChange(e.target as HTMLInputElement)} 
                accept=".csv"  
              />
            </Form.Group>
            {csvFile && (
            <div className="w-100 mt-3">
              <Alert variant="info" className="d-flex align-items-center mb-3 py-2 px-3 csv-mapping-info-banner">
                <FontAwesomeIcon icon={faCircleInfo} className="me-2 text-info fs-5 flex-shrink-0" />
                <div>
                  <div className="fw-semibold">Please map CSV fields with system fields</div>
                  <div className="small text-muted">
                    Match each column from your CSV file with an existing system field before saving. Unmapped fields will not be imported into the system.
                  </div>
                </div>
              </Alert>

              <div className='d-flex justify-content-between align-items-center mb-1'>
                <div className='d-flex w-100'>
                  <h6 className='w-50 mb-0'>CSV File Fields</h6>
                  <h6 className='w-50 mb-0'>Existing Fields</h6>
                </div>
              </div>
              {unmappedFields.length > 0 ? (
                <div className="text-warning small mb-3 fw-medium">
                  ⚠ {unmappedFields.length} of {csvFields.length} field{unmappedFields.length > 1 ? 's' : ''} currently unmapped
                </div>
              ) : (
                <div className="text-success small mb-3 fw-medium">
                  ✓ All {csvFields.length} fields mapped
                </div>
              )}
              <div className="d-flex flex-column">
                {csvFields.map((csvField: any, index: any) => (
                  <div
                    key={index}
                    className="d-flex mb-3 justify-content-between align-items-center"
                  >
                    {/* CSV Field label */}
                    <div className="csv-mapping-field">
                      <span>{csvField}</span>
                    </div>

                    <div className="d-flex justify-content-center align-items-center">
                      <FontAwesomeIcon icon={faArrowRight} className="mx-2" />
                    </div>

                    {/* Existing Fields dropdown */}
                    <div className="w-50">
                      <Form.Select
                        onChange={(e) =>
                        {const selectedValue = e.target.value;
                          if (selectedValue === "custom-field") {
                            setShowCustomFieldsModal(true);
                          } else {
                            handleFieldChange(csvField, selectedValue, index);
                          }}
                        }
                        className="form-select"
                        value={preselectedValue[index]?.databaseField || ""}
                      >
                        <option value="">Select a field</option>
                        {existingFields
                          .filter(
                            (option: any) =>
                              !Object.values(preselectedValue)
                                .map((mapped: any) => mapped.databaseField)
                                .includes(option) ||
                              preselectedValue[index]?.databaseField ===
                                option
                          )
                          .map((option: any, optionIndex: any) => (
                            <option key={optionIndex} value={option}>
                              {option}
                            </option>
                          ))}
                          <option value="custom-field" onClick={()=>{setShowCustomFieldsModal(true)}}>Add Custom Field</option>
                      </Form.Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </Modal.Body>
          <Modal.Footer className='justify-content-between'>
            <Button 
              variant="primary" 
              className='btn-theme btn-border' 
              disabled={isUploading}
              onClick={()=>{
                handleClose()
                setCsvFile(null)
              }} >
              Close
            </Button>
            <XButton
              variant="primary"
              type="submit"
              buttonText={<>Save & Continue<FontAwesomeIcon icon={faPlay} /></>}
              loading={loading}
              loadingText={isUploading ? `${uploadProgress}% Processing...` : 'Loading...'}
              disabled={isUploading && uploadProgress >= 100}
            /> 
          </Modal.Footer>
          </Form>
        </Modal>

        <XConfirm
          show={showCsvConfirmModal}
          onCancel={() => setShowCsvConfirmModal(false)}
          onConfirm={handleProceedSaveList}
          title="Confirm Field Mapping"
          className="csv-confirm-modal"
          confirmLabel="Yes, Save & Continue"
          cancelLabel="Review Mapping"
          message={
            <div>
              <p className="mb-2">
                Please make sure you have mapped all CSV fields with system fields before saving.
              </p>
              {unmappedFields.length > 0 ? (
                <div className="alert alert-warning py-2 px-3 mb-2 small">
                  <strong>Notice:</strong> You have <strong>{unmappedFields.length}</strong> unmapped field{unmappedFields.length > 1 ? 's' : ''} ({unmappedFields.slice(0, 4).join(', ')}{unmappedFields.length > 4 ? ` and ${unmappedFields.length - 4} more` : ''}). Unmapped fields will not be imported.
                </div>
              ) : (
                <div className="alert alert-success py-2 px-3 mb-2 small">
                  ✓ All CSV fields are successfully mapped to system fields.
                </div>
              )}
              <p className="mb-0 text-muted small">
                Are you sure you want to save and continue?
              </p>
            </div>
          }
        />

        {/* Custom Fields Modal */}
      <Modal
        show={showCustomFieldsModal}
        onHide={() => {
          customFieldsFormik.resetForm();
          setShowCustomFieldsModal(false)}
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Custom Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={customFieldsFormik.handleSubmit}>
            <Row>
              <Col xl={6} md={6} xs={6}>
                <Form.Group>
                  <Form.Control
                    name="customFields[0].columnName"
                    type="text"
                    placeholder="Custom Name"
                    className="form-control mt-2"
                    value={customFieldsFormik.values.customFields[0].columnName}
                    onChange={customFieldsFormik.handleChange}
                    isInvalid={customFieldsFormik.touched.customFields && customFieldsFormik.touched.customFields[0] && !!((customFieldsFormik.errors.customFields as any)?.[0]?.columnName)}
                  />
                  <Form.Control.Feedback type="invalid">{((customFieldsFormik.errors.customFields as any)?.[0]?.columnName)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xl={6} md={6} xs={6}>
                <Form.Group>
                  <Form.Select
                    name="customFields[0].columnType"
                    className="form-control mt-2"
                    value={customFieldsFormik.values.customFields[0]?.columnType}
                    onChange={customFieldsFormik.handleChange}
                    onBlur={customFieldsFormik.handleBlur}
                    isInvalid={customFieldsFormik.touched.customFields && customFieldsFormik.touched.customFields[0] && !!((customFieldsFormik.errors.customFields as any)?.[0]?.columnType)}
                  >
                    <option value="">Column Type</option>
                    {columnTypes.map((type, i) => (
                      <option key={i} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{((customFieldsFormik.errors.customFields as any)?.[0]?.columnType)}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <br/>
              <div className='d-flex justify-content-between align-items-center'>
                <XButton
                  variant="primary"
                  type="submit"
                  buttonText="Submit"
                />
              </div>
            <br/>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View & Search All Contacts Modal */}
      <ContactsViewerModal
        show={showContactsModal}
        onHide={() => setShowContactsModal(false)}
        contacts={selectedOptions}
        onRemoveContact={handleRemoveContactFromSelected}
      />
    </div>
    {/* <div className="info-cards">
      <Row>
        <Col xl={4}>
          <div className="card">
            <h2 className='title'>Total Contacts</h2>
            <div className="text text-center">
              <span className='count'>0</span>
              <Button
                variant="primary"
                type="submit"
                className="btn-theme"
              >Export Contacts
              </Button>
            </div>
          </div>
        </Col>
        <Col xl={4}>
          <div className="card">
            <h2 className='title'>Emails Sent</h2>
            
          </div>
        </Col>
        <Col xl={4}>
          <div className="card">
            <h2 className='title'>Unsubscribes</h2>
            <div className="d-flex justify-content-between">
              <div className="text text-center">
                <span className='count'>0</span>
                <p className='text'>List Unsubscribes</p>
                <p className='sub-text'>0% Unsubscribe Rate</p>
              </div>
              <div className="text text-center">
                <span className='count'>0</span>
                <p className='text'>Global Unsubscribes</p>
                <p className='sub-text'>0% Unsubscribe Rate</p>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div> */}
    <div className="table-listing contact-list position-relative">
      <Table responsive>
        <thead>
          <tr>
          {tableHeadings_List.map((heading, index) => (
            <th key={index} 
              className={`${heading === "Actions" ? "" : "cursor-pointer"}`}
              onClick={() => handleSortClick(heading)}>
              <span style={{ marginRight: '5px' }}>{heading}</span>
              {heading !== 'Actions' && (
                <FontAwesomeIcon icon={sortColumn === heading && sortOrder === 'desc' ? faSortUp : (sortColumn === heading && sortOrder === 'asc' ? faSortDown : faSort)} />
              )}
            </th>
          ))}
          </tr>
        </thead>
        
        <tbody>
        {!loading && sortedList?.length === 0 &&
          <tr>
            <td colSpan={9}>
              <p className='text-center no-records'>No list found.</p>
            </td>
          </tr>
        }
        {!loading && sortedList && sortedList?.map((list: any) => (
              <tr key={list.id}>
                <td>
                  <span className='d-flex'>
                    <span className="d-flex align-items-center text-darkBlue">
                    {list.name}
                    </span>
                  </span>
                </td>
                <td>{list.contact_count}</td>
                <td>{formatDate(list.created_at, locationTimezone)}</td>
                <td className='action-badges'>
              {/* <button  onClick={() => handleDeleteList(list.id)}>
                <span className='d-flex align-items-center'>
                  Delete <FontAwesomeIcon icon={faCircleXmark} />
                </span>
              </button> */}
              {(permission?.delete) && (
                <button onClick={() => handleDeleteIconClick(list)}>
                  <span className='d-flex align-items-center'>
                  <span>Delete <FontAwesomeIcon icon={faCircleXmark} /></span>
                    
                  </span>
                </button>
              )}
              {(permission?.edit) && (
                <button className='edit' onClick={() => handleEditList(list.id)}>
                  <span className='d-flex align-items-center' >
                    Edit <FontAwesomeIcon icon={faPenToSquare} />
                  </span>
                </button>
              )}
            </td>
              </tr>
            ))}
        </tbody>
      </Table>
      {loading && (<XLoader />)}
    </div>
    <XDeleteModal
                    show={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    title="Delete List"
                    body={`Are you sure you want to delete the List?`}
                    onConfirm={() => {
                      if (selectedList) {
                        handleDeleteList(selectedList.id);
                      }
                      setShowDeleteModal(false);
                    }}
                    onCancel={() => setShowDeleteModal(false)}
                  />
      {!loading && sortedList?.length !== 0 && (
        <PaginationNav
          totalPages={totalPages}
          currentPage={queryParams.page}
          handlePageChange={handlePageChange}
        />
      )}
      
  </div>
  )
}

export default Lists;
